import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import {
  useAssignConsumer,
  useConsumers,
} from "@features/mx/device-management/hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { queryClient } from "@shared/api/queries/queryClient";
import { useParams } from "react-router-dom";
import {MODAL_PAGE_SIZE as PAGE_SIZE } from "@shared/utils/constants";

interface UpdateConsumerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resource: any;
}

export const UpdateConsumerModal: React.FC<UpdateConsumerModalProps> = ({
  open,
  onOpenChange,
  resource,
}) => {
  const { id } = useParams();
  const [selectedConsumer, setSelectedConsumer] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const { data: consumersData } = useConsumers(
    currentPage,
    PAGE_SIZE,
    1,
    searchTerm 
  );
  
  const { mutate: assignConsumer } = useAssignConsumer();

  const totalItems = consumersData?.count || 0;

  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages: Math.ceil(totalItems / PAGE_SIZE) || 1,
      totalItems: totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1,
      endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
    }),
    [currentPage, totalItems]
  );

  // Handle search from GenericTable
  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1); 
  };

  // Handle consumer selection
  const handleSelectConsumer = (consumer: any) => {
    setSelectedConsumer(consumer.id === selectedConsumer?.id ? null : consumer);
  };

  const handleAssign = () => {
    if (selectedConsumer) {
      const payload = {
        remote_utility_id: getRemoteUtilityId(),
        meter_number: resource?.deviceNo || resource?.id,
        consumer_no: selectedConsumer.consumerNo,
      };

      assignConsumer(payload, {
        onSuccess: () => {
          onOpenChange(false);
          queryClient.refetchQueries({
          queryKey:["mx", "meter-detail", id,String(getRemoteUtilityId())],
        });
        },
      });
    }
  };

  // Table columns
  const columns: TableColumn<any>[] = useMemo(
    () => [
      {
        key: "consumerNo",
        header: "Account number",
        sortable: true,
      },
      {
        key: "name",
        header: "Name",
        sortable: true,
        render: (_, row) => `${row.firstName} ${row.lastName}`,
      },
      {
        key: "categoryDisplay",
        header: "Category",
        sortable: true,
      },
      {
        key: "subCategoryDisplay",
        header: "Sub category",
        sortable: true,
      },
      {
        key: "premise",
        header: "Premise",
        sortable: true,
        render: (_, row) => row.territoryData?.service?.premise || "—",
      },
      {
        key: "action",
        header: "Action",
        width: "w-24",
        sortable: false,
        render: (_, row) => (
          <Button
            variant={selectedConsumer?.id === row.id ? "default" : "outline"}
            size="sm"
            onClick={() => handleSelectConsumer(row)}
          >
            {selectedConsumer?.id === row.id ? "Selected" : "Select"}
          </Button>
        ),
      },
    ],
    [selectedConsumer]
  );

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedConsumer(null);
      setSearchTerm("");
      setCurrentPage(1);
    }
  }, [open]);

  if (resource?.physicalStatus !== "Installed") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Assign consumer</DialogTitle>
          </DialogHeader>
          <div className="py-6 text-center">
            <div className="text-amber-800 bg-amber-50 rounded-lg p-4 border border-amber-200 text-sm font-medium">
              Please assign a unit to this resource to update the consumer.
            </div>
          </div>
          <DialogFooter>
            <Button className="w-full" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Assign consumer</DialogTitle>
          <DialogDescription>
            Search and select a consumer to link to this resource.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <GenericTable
            data={consumersData?.results || []}
            columns={columns}
            rowKey="id"
            searchPlaceholder="Search by name or account number..."
            searchParamName="search_data"
            searchDebounceMs={500}
            onSearch={handleSearch}
            emptyMessage="No consumers found"
            pagination={pagination}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>

        <DialogFooter className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={!selectedConsumer}>
            Assign consumer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
